import { resolveGrowthEngineCtaPath } from './growthEngineCtaRoutes'
import { resolveOllieFindingTargetPath } from './ollieFindingCards'

/**
 * Phase 4 — what the Ollie panel does when someone taps a chip, a button on
 * one of Ollie's replies, or confirms prepared work. Pure, so the panel and
 * sheets only render.
 *
 * Nothing here saves. Prepared work is saved by Express only after the
 * person presses the confirm button; supplier messages are drafts they send
 * themselves.
 */

export const OLLIE_TAP_TYPES = Object.freeze({
  ASK: 'ask',
  NAVIGATE: 'navigate',
  ACTION: 'action',
  NONE: 'none',
})

export const OLLIE_SHEET_KINDS = Object.freeze({
  PARS: 'pars',
  STOCK_TAKE: 'stock_take',
  BUSINESS_DAYS: 'business_days',
  RECIPE_DRAFT: 'recipe_draft',
  DRAFTS: 'drafts',
  NOTHING: 'nothing',
})

export const OLLIE_ACTION_COPY = Object.freeze({
  cancel: 'Not now',
  close: 'Close',
  preparing: 'Ollie is getting this ready…',
  confirming: 'Saving…',
  nothingSaved: 'Nothing is saved until you confirm.',
  copy: 'Copy',
  copied: 'Copied',
  openEmail: 'Open in email',
  openWhatsApp: 'Open in WhatsApp',
  to: 'To',
  subject: 'Subject',
  period: 'Period',
  from: 'From',
  until: 'To',
  products: 'Products you buy most (untick any to leave out)',
  pickDay: 'Pick at least one day.',
  parInvalid: 'Pars must be a number from 0 to 999.',
  keepOne: 'Keep at least one product.',
  upgrade: 'View upgrade options',
  reportLink: "This isn't right",
  reportTitle: "What's not right?",
  reportComment: 'Tell us a bit more',
  reportCommentRequired: 'Tell us a bit more so we can look into it.',
  reportSend: 'Send report',
  reportPickReason: 'Pick a reason.',
  emailStephen: 'Email Stephen',
  freeFollowUps: {
    one: "1 more question about this today won't count towards your Lite messages.",
    many: "{n} more questions about this today won't count towards your Lite messages.",
    none: 'More questions about this today count towards your Lite messages.',
  },
})

/** ISO weekdays (1 = Monday … 7 = Sunday), as Express saves them. */
export const OLLIE_WEEKDAYS = Object.freeze([
  Object.freeze({ day: 1, label: 'Mon' }),
  Object.freeze({ day: 2, label: 'Tue' }),
  Object.freeze({ day: 3, label: 'Wed' }),
  Object.freeze({ day: 4, label: 'Thu' }),
  Object.freeze({ day: 5, label: 'Fri' }),
  Object.freeze({ day: 6, label: 'Sat' }),
  Object.freeze({ day: 7, label: 'Sun' }),
])

const PAR_MIN = 0
const PAR_MAX = 999

/**
 * A finding chip. Why? / How? are questions; Show me opens the screen;
 * Help me / Do it for me prepare the finding's action for review. A Show me
 * with no dashboard screen does nothing, so the panel leaves it out.
 */
export function resolveOllieChipTap(chip, venueId) {
  if (!chip?.id) return { type: OLLIE_TAP_TYPES.NONE }
  if (chip.kind === 'ask') return { type: OLLIE_TAP_TYPES.ASK }
  const action = chip.action
  if (!action?.id) return { type: OLLIE_TAP_TYPES.NONE }
  if (chip.kind === 'open' || action.kind === 'open') {
    const path = resolveOllieFindingTargetPath(
      venueId,
      action.target,
      action.params,
    )
    return path
      ? { type: OLLIE_TAP_TYPES.NAVIGATE, path, action }
      : { type: OLLIE_TAP_TYPES.NONE }
  }
  return { type: OLLIE_TAP_TYPES.ACTION, action }
}

export function listUsableOllieChips(chips = [], venueId, messages = []) {
  const asked = askedOllieChipIds(messages)
  return (chips || []).filter(
    chip =>
      !(chip.kind === 'ask' && asked.has(chip.id)) &&
      resolveOllieChipTap(chip, venueId).type !== OLLIE_TAP_TYPES.NONE,
  )
}

/**
 * Thread chips minus any that open the same screen as a button on Ollie's
 * latest reply ("Open stock take" and Show me side by side): one way there.
 */
export function withoutChipsMatchingReply(
  chips = [],
  replyActions = [],
  venueId,
) {
  const replyPaths = new Set(
    (replyActions || [])
      .map(action => resolveOllieActionTap(action, venueId))
      .filter(tap => tap.type === OLLIE_TAP_TYPES.NAVIGATE)
      .map(tap => tap.path),
  )
  if (!replyPaths.size) return chips || []
  return (chips || []).filter(chip => {
    const tap = resolveOllieChipTap(chip, venueId)
    return !(tap.type === OLLIE_TAP_TYPES.NAVIGATE && replyPaths.has(tap.path))
  })
}

/**
 * Why? / How? already asked in this thread, from the moment they're tapped.
 * A question Ollie never answered (failed or timed out) can be asked again.
 */
export function askedOllieChipIds(messages = []) {
  const list = messages || []
  const asked = new Set()
  list.forEach((message, index) => {
    if (message?.role !== 'user' || !message.chipId || message.unanswered) {
      return
    }
    const reply = list[index + 1]
    if (reply?.role === 'assistant' && reply.timedOut) return
    asked.add(message.chipId)
  })
  return asked
}

/** A button on one of Ollie's replies (or a card button): open, else prepare. */
export function resolveOllieActionTap(action, venueId) {
  if (!action?.id) return { type: OLLIE_TAP_TYPES.NONE }
  if (action.kind === 'open') {
    const path = resolveOllieFindingTargetPath(
      venueId,
      action.target,
      action.params,
    )
    return path
      ? { type: OLLIE_TAP_TYPES.NAVIGATE, path, action }
      : { type: OLLIE_TAP_TYPES.NONE }
  }
  if (action.kind === 'prepare' || action.kind === 'draft') {
    return { type: OLLIE_TAP_TYPES.ACTION, action }
  }
  return { type: OLLIE_TAP_TYPES.NONE }
}

/** Which confirm sheet fits the prepared work (by its shape, not the action id). */
export function ollieSheetKind(prepared) {
  if (!prepared) return OLLIE_SHEET_KINDS.NOTHING
  if (Array.isArray(prepared.drafts)) {
    return prepared.drafts.length
      ? OLLIE_SHEET_KINDS.DRAFTS
      : OLLIE_SHEET_KINDS.NOTHING
  }
  if (!prepared.confirm) return OLLIE_SHEET_KINDS.NOTHING
  if (prepared.handoff && Array.isArray(prepared.dishes)) {
    return OLLIE_SHEET_KINDS.RECIPE_DRAFT
  }
  if (Array.isArray(prepared.weekdays)) return OLLIE_SHEET_KINDS.BUSINESS_DAYS
  if (prepared.period?.startDate) return OLLIE_SHEET_KINDS.STOCK_TAKE
  if (Array.isArray(prepared.items) && prepared.items.length) {
    return OLLIE_SHEET_KINDS.PARS
  }
  return OLLIE_SHEET_KINDS.NOTHING
}

/** Editable par rows, pre-filled with what they usually order. */
export function buildOllieParRows(prepared) {
  return (prepared?.items || [])
    .filter(item => item?.productId)
    .map(item => ({
      productId: String(item.productId),
      title: item.title || '',
      UoM: item.UoM || null,
      orderCount: Number.isInteger(item.orderCount) ? item.orderCount : null,
      value: item.suggestedPar == null ? '' : String(item.suggestedPar),
      keep: true,
    }))
}

export function isValidOlliePar(value) {
  if (value === '' || value == null) return false
  const n = Number(value)
  return Number.isFinite(n) && n >= PAR_MIN && n <= PAR_MAX
}

/** Kept rows → confirm `items`, or the reason we can't save yet. */
export function buildOllieParsConfirm(rows = []) {
  const kept = rows.filter(row => row.keep)
  if (!kept.length) return { ok: false, message: OLLIE_ACTION_COPY.keepOne }
  if (kept.some(row => !isValidOlliePar(row.value))) {
    return { ok: false, message: OLLIE_ACTION_COPY.parInvalid }
  }
  return {
    ok: true,
    items: kept.map(row => ({
      productId: row.productId,
      parLevel: Number(row.value),
    })),
  }
}

/** Same wording as Express `OLLIE_PARS_ACTION_COPY.confirmButton`. */
export function ollieParsButtonLabel(count) {
  return count === 1 ? 'Set 1 par' : `Set ${count} pars`
}

export function buildOllieStockTakeRows(prepared) {
  return (prepared?.items || [])
    .filter(item => item?.productId)
    .map(item => ({
      productId: String(item.productId),
      title: item.title || '',
      keep: true,
    }))
}

const DATE_KEY = /^\d{4}-\d{2}-\d{2}$/

/** Confirm fields for a stock take: dates + the suggested products kept. */
export function buildOllieStockTakeConfirm({ startDate, endDate, rows = [] }) {
  if (!DATE_KEY.test(startDate || '') || !DATE_KEY.test(endDate || '')) {
    return { ok: false, message: 'Pick a start and end date.' }
  }
  if (startDate > endDate) {
    return {
      ok: false,
      message: 'Start date must be before or equal to end date.',
    }
  }
  return {
    ok: true,
    fields: {
      startDate,
      endDate,
      productIds: rows.filter(row => row.keep).map(row => row.productId),
    },
  }
}

export function toggleOllieWeekday(weekdays = [], day) {
  const set = new Set(weekdays)
  if (set.has(day)) set.delete(day)
  else set.add(day)
  return [...set].sort((a, b) => a - b)
}

export function buildOllieBusinessDaysConfirm(weekdays = []) {
  const valid = weekdays.filter(
    day => Number.isInteger(day) && day >= 1 && day <= 7,
  )
  if (!valid.length) return { ok: false, message: OLLIE_ACTION_COPY.pickDay }
  return { ok: true, fields: { weekdays: valid } }
}

/** Recipe drafts are saved in Menu Builder: open the dish there. */
export function ollieRecipeHandoffPath(venueId, dish) {
  if (!venueId || !dish?.recipeId) return null
  return resolveGrowthEngineCtaPath(venueId, 'open_menu_builder', {
    recipeId: dish.recipeId,
  })
}

/** "2 more questions about this today won't count…" (Free finding threads). */
export function ollieFreeFollowUpLine(followUp) {
  if (!followUp?.quotaExempt) return null
  const left = followUp.freeLeftToday
  if (!Number.isInteger(left)) return null
  if (left <= 0) return OLLIE_ACTION_COPY.freeFollowUps.none
  if (left === 1) return OLLIE_ACTION_COPY.freeFollowUps.one
  return OLLIE_ACTION_COPY.freeFollowUps.many.replace('{n}', String(left))
}

export function validateOllieReport({ reason, comment = '' } = {}) {
  if (!reason) return { ok: false, message: OLLIE_ACTION_COPY.reportPickReason }
  if (reason === 'other' && !String(comment).trim()) {
    return { ok: false, message: OLLIE_ACTION_COPY.reportCommentRequired }
  }
  return { ok: true }
}
